import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X, RefreshCw } from 'lucide-react';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import ConfirmationModal from '../../common/ConfirmationModal';
import { formatDateToDdmmyyyy } from '../../../utils/format';
import {
  FarmExecutiveRow, FarmPlanPeriod, farmBasisLabel, farmPlanPeriods,
} from './farmIncentivesView';
import {
  MarketingOrderRow, currentPlanPeriod, isBelowFirstSlab, monthLabel, recentMonthYears,
} from './marketingOrdersView';
import FarmExecutiveIncentivesCard, { farmKey } from './FarmExecutiveIncentivesCard';
import { OrderColumn } from './MarketingOrdersTable';
import { ORDERS_PAGE_SIZE } from './useExpandedOrders';
import Snackbar from '../../common/Snackbar';
import Select from '../../common/Select';

const formatINR = (val?: number) => '₹' + Number(val || 0).toLocaleString('en-IN');

interface FarmOrdersSummary {
  visited_orders?: number;
  farm_incentives?: number;
  pending_farm_incentives?: number;
}

/**
 * Farm Orders — the same flow as Marketing Orders: filters, tiles, and a
 * farm-executive-wise table whose View opens that executive-month's orders
 * beneath the row (same columns as Marketing Orders, with the farm incentive
 * and slab at the end). Farm incentives are approved for the whole
 * executive-month at once (Approve all), by a Marketing Manager only.
 */
const FarmOrdersTab: React.FC = () => {
  const { adminMobile, adminRole } = useAppSelector((s: RootState) => s.auth);
  // Only a Marketing Manager gets Approve all — everyone else, Admin and
  // SuperAdmin included, can still view this tab with no Approve button
  // (the API refuses them too).
  const isMarketingManager = /marketing[_ ]?manager/i.test(adminRole || '');

  const [summary, setSummary] = useState<FarmOrdersSummary | null>(null);
  // Farm-executive-wise, month-wise farm slabs and incentives.
  const [farmExecRows, setFarmExecRows] = useState<FarmExecutiveRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [marketingStaff, setMarketingStaff] = useState<any[]>([]);
  const [farmStaff, setFarmStaff] = useState<{ mobile: string; name?: string }[]>([]);

  // The farm executive-month whose Approve all is awaiting confirmation.
  // Approving credits real money and there is no un-approve endpoint, so the
  // click only opens this dialog — the POST happens on confirm.
  const [confirmExec, setConfirmExec] = useState<FarmExecutiveRow | null>(null);
  const [bulkApprovingKey, setBulkApprovingKey] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // The monthly farm plan the page is divided by: tiles and the farm
  // executive table narrow to orders whose incentive falls in its dates
  // (approval date, else placed), priced on that month's farm ladder.
  // '' = all periods; null = plans not loaded yet, so the first load waits.
  const [periods, setPeriods] = useState<FarmPlanPeriod[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const selectedPeriod = periods.find((p) => p.plan_id === planId) || null;

  useEffect(() => {
    const fetchPlans = async () => {
      if (!adminMobile) return;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      try {
        const res = await marketingService.listFarmIncentivePlans(adminMobile);
        const plans = res?.plans || [];
        const list = farmPlanPeriods(plans, today);
        setPeriods(list);
        setPlanId((cur) => (cur !== null && cur !== '' ? cur : (currentPlanPeriod(list, today)?.plan_id || (list.length > 0 ? list[0].plan_id : ''))));
      } catch (err) {
        console.error('Error fetching farm incentive plans:', err);
        setPeriods([]);
        setPlanId((cur) => (cur !== null && cur !== '' ? cur : ''));
      }
    };
    fetchPlans();
  }, [adminMobile]);

  // Names for the drill-down's Lead By / Farm Executive columns.
  useEffect(() => {
    if (!adminMobile) return;
    marketingService.listStaff(adminMobile)
      .then((res: any) => setMarketingStaff(res?.staff || []))
      .catch(() => setMarketingStaff([]));
    marketingService.listFarmStaff(adminMobile)
      .then((res: any) => setFarmStaff(res?.staff || []))
      .catch(() => setFarmStaff([]));
  }, [adminMobile]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!adminMobile || planId === null) return;
    setLoading(true);
    setError(null);
    try {
      // Tiles and the farm executive table only; an executive-month's orders
      // are fetched when its row is opened (loadExecOrders).
      const res = await marketingService.getFarmOrdersSummary(adminMobile, {
        search: debouncedSearch || undefined,
        incentiveFrom: selectedPeriod?.from,
        incentiveTo: selectedPeriod?.to,
      });
      if (res?.statuscode === 200) {
        setFarmExecRows(res.farm_executive_incentives || []);
        setSummary({
          visited_orders: res.visited_orders,
          farm_incentives: res.farm_incentives,
          pending_farm_incentives: res.pending_farm_incentives,
        });
      } else {
        setError(res?.message || 'Failed to load orders');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, planId, selectedPeriod?.from, selectedPeriod?.to, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  // The orders behind one row of the farm executive table — exactly what the
  // row counted (who scanned the visitor in, and the incentive month), every
  // payment status, so the ineligible ones show why.
  const loadExecOrders = useCallback(async (row: FarmExecutiveRow, ordersPage: number) => {
    if (!adminMobile) return { orders: [], totalPages: 1 };
    const res = await marketingService.listFarmExecutiveOrders(adminMobile, {
      farmExecMobile: row.exec_mobile,
      month: row.month,
      search: debouncedSearch || undefined,
      incentiveFrom: selectedPeriod?.from,
      incentiveTo: selectedPeriod?.to,
      page: ordersPage,
      page_size: ORDERS_PAGE_SIZE,
    });
    if (res?.statuscode !== 200) throw new Error(res?.message || res?.detail || 'Failed to load orders');
    return { orders: (res.orders || []) as MarketingOrderRow[], totalPages: res.total_pages || 1 };
  }, [adminMobile, selectedPeriod?.from, selectedPeriod?.to, debouncedSearch]);

  const handleBulkApprove = async (row: FarmExecutiveRow) => {
    if (!adminMobile) return;
    setBulkApprovingKey(farmKey(row));
    try {
      const res = await marketingService.approveFarmIncentivesBulk(adminMobile, row.exec_mobile, row.month);
      if (res?.statuscode && res.statuscode !== 200) {
        throw new Error(res.detail || res.message || 'Approval failed');
      }
      const who = row.exec_name || row.exec_mobile;
      setSnackbar(res?.complete === false
        ? {
          message: `Approved ${res.approved_count} of ${res.approved_count + res.remaining} for ${who}; stopped at ${res.failed_order_id}: ${res.error}`,
          type: 'error',
        }
        : {
          message: `Approved ${res?.approved_count ?? row.pending_orders} farm incentive(s) for ${who} · ${monthLabel(row.month)}`,
          type: 'success',
        });
      // Reloads the table, which also refreshes the open row's orders.
      load();
    } catch (e: any) {
      setSnackbar({
        message: e?.response?.data?.detail || e?.message || 'Approval failed',
        type: 'error',
      });
    } finally {
      setBulkApprovingKey(null);
      setConfirmExec(null);
    }
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <div className="px-4 py-2.5 flex items-baseline justify-between gap-4">
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="text-[13px] font-semibold text-slate-700 text-right">{value}</span>
    </div>
  );

  // A Marketing Manager, with something pending, on a month that has reached
  // the first band — and never their own farm incentives (the backend's
  // _guard_self_approval; another Marketing Manager must sign those off).
  const canApproveRow = (r: FarmExecutiveRow) =>
    isMarketingManager
    && r.pending_orders > 0
    && !isBelowFirstSlab(r.slab)
    && r.exec_mobile !== adminMobile;

  // The drill-down's last columns: everything before them is the Marketing
  // Orders table, unchanged.
  const farmColumns: OrderColumn[] = useMemo(() => [
    {
      header: 'Farm Incentive',
      render: (o) => (o.farm_incentive?.amount ? (
        <div className="flex flex-col gap-0.5">
          <span className="font-black text-slate-800 text-[13px]">{formatINR(o.farm_incentive.amount)}</span>
          {farmBasisLabel(o.farm_incentive.projection_basis) && (
            <span className="text-[10px] font-semibold text-slate-400">
              {farmBasisLabel(o.farm_incentive.projection_basis)}
            </span>
          )}
        </div>
      ) : (
        <span className="text-slate-300 text-[11px] cursor-help"
          title={o.farm_incentive?.reason || 'No farm visit on this lead.'}>
          {o.farm_incentive?.status === 'NOT_ELIGIBLE' ? 'Not eligible' : '—'}
        </span>
      )),
    },
    {
      header: 'Farm Slab',
      render: (o) => {
        if (!o.farm_incentive?.slab) return <span className="text-slate-300">—</span>;
        
        const breakdowns = o.farm_incentive.slab_breakdown;
        if (breakdowns && breakdowns.length > 0) {
          return (
            <div className="flex flex-col gap-2">
              {breakdowns.map((b, i) => (
                <div key={i} className="flex flex-col gap-0.5">
                  <span className="font-semibold text-slate-700 text-[11px] leading-tight">
                    {b.units} unit{b.units > 1 ? 's' : ''} in {b.slab_range} (₹{b.amount})
                  </span>
                  {b.slab_id && (
                    <span className="font-mono text-slate-400 text-[10px] leading-tight">{b.slab_id}</span>
                  )}
                </div>
              ))}
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-slate-700 text-[12px]">{o.farm_incentive.slab}</span>
            {o.farm_incentive.slab_id && (
              <span className="font-mono text-slate-400 text-[11px]">{o.farm_incentive.slab_id}</span>
            )}
          </div>
        );
      },
    },
  ], []);

  const tile = (label: string, value: React.ReactNode, tone = 'text-slate-900') => (
    <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col justify-between min-w-[240px] shrink-0 flex-1 snap-start">
      <div className="mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className={`font-black text-2xl tracking-tight whitespace-nowrap ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      {/* 1. Filters Bar — search on the left, period/clear/refresh on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:w-auto min-w-0 sm:min-w-[280px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
          <input
            type="text"
            placeholder="Search by Order ID, Mobile, Name, or Lead phone/name/ID"
            className="h-[42px] pl-10 pr-8 w-full text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 outline-none focus:border-[#1B4332] transition-all duration-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 font-bold bg-transparent border-none cursor-pointer"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          {periods.length > 0 && (
            <Select
              className="w-auto h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 transition-all duration-200 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              value={planId || ''}
              onChange={(v) => setPlanId(v)}
              options={[
                ...periods.map((p) => ({
                  value: p.plan_id,
                  label: (
                    <div className="flex flex-col text-left">
                      <span className="text-[13px] font-bold leading-tight">{p.name}</span>
                      <span className="text-[11px] font-medium leading-tight opacity-75 mt-[2px]">{formatDateToDdmmyyyy(p.from)} – {formatDateToDdmmyyyy(p.to)}</span>
                    </div>
                  ),
                })),
              ]}
              renderValue={(selected) => {
                if (!selected || selected.value === '') return null;
                const p = periods.find((x) => x.plan_id === selected.value);
                if (!p) return null;
                return (
                  <div className="flex flex-col items-start text-left">
                    <span className="text-[13px] font-bold text-slate-800 leading-tight">{p.name}</span>
                    <span className="text-[11px] text-slate-500 font-medium leading-tight mt-[2px]">{formatDateToDdmmyyyy(p.from)} – {formatDateToDdmmyyyy(p.to)}</span>
                  </div>
                );
              }}
            />
          )}
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="flex items-center justify-center h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
            className={`flex items-center justify-center gap-2 h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* 2. Stat Cards — over the whole filtered set; the money tiles are the
          farm executive table's own totals. */}
      {loading && !summary ? (
        <div className="flex gap-4 w-full overflow-x-auto pb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs animate-pulse min-w-[240px] shrink-0 flex-1">
              <div className="h-3 w-24 bg-slate-200 rounded-md mb-3" />
              <div className="h-7 w-32 bg-slate-200 rounded-md" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="flex gap-4 w-full overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent snap-x">
          {tile('Visited Orders', (summary.visited_orders || 0).toLocaleString('en-IN'))}
          {tile('Farm Incentives Given', formatINR(summary.farm_incentives), 'text-emerald-700')}
          {tile('Pending Farm Incentives', formatINR(summary.pending_farm_incentives), 'text-amber-700')}
        </div>
      ) : null}

      {/* 3. Farm executive-wise orders and farm slabs */}
      {error ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-rose-500 font-medium">
          {error}
        </div>
      ) : (
        <FarmExecutiveIncentivesCard
          rows={farmExecRows}
          loadOrders={loadExecOrders}
          incentiveColumns={farmColumns}
          canApprove={canApproveRow}
          approvingKey={bulkApprovingKey}
          onApprove={setConfirmExec}
          marketingStaff={marketingStaff}
          farmStaff={farmStaff}
          adminMobile={adminMobile || undefined}
          hasApproveAccess={isMarketingManager}
        />
      )}
      {!loading && !error && summary && farmExecRows.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 text-center text-sm text-slate-400 font-medium">
          {search ? 'No visited orders found matching your filters.' : 'No visited orders yet.'}
        </div>
      )}

      {/* Approve a whole farm executive-month. Same finality as a single order. */}
      <ConfirmationModal
        isOpen={!!confirmExec}
        title="Approve all pending farm incentives?"
        description={confirmExec
          ? `This credits ${formatINR(confirmExec.pending_amount)} to ${confirmExec.exec_name || confirmExec.exec_mobile} for ${confirmExec.pending_orders} order(s) in ${monthLabel(confirmExec.month)}, oldest first. This is separate from the orders' marketing incentives. Approval is final — an incentive cannot be un-approved.`
          : ''}
        confirmLabel="Yes, approve all"
        cancelLabel="Cancel"
        loading={!!confirmExec && bulkApprovingKey === farmKey(confirmExec)}
        onConfirm={() => { if (confirmExec) handleBulkApprove(confirmExec); }}
        onClose={() => setConfirmExec(null)}
        details={confirmExec ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 divide-y divide-slate-200/80">
            <div className="px-4 py-3 flex items-baseline justify-between gap-4">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Pending</span>
              <span className="text-lg font-black text-emerald-700">{formatINR(confirmExec.pending_amount)}</span>
            </div>
            {detailRow('Farm executive', confirmExec.exec_name || confirmExec.exec_mobile)}
            {detailRow('Month', monthLabel(confirmExec.month))}
            {detailRow('Orders', `${confirmExec.pending_orders} awaiting approval · ${confirmExec.pending_units} sales`)}
            {detailRow('Farm slab', confirmExec.slab || '—')}
          </div>
        ) : undefined}
      />

      {snackbar && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}
    </div>
  );
};

export default FarmOrdersTab;
